import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../i18n";
import {
  LayoutDashboard, FileStack, ClipboardList, BellRing, Receipt, Landmark,
  CalendarClock, AlertCircle, CheckCircle2,
} from "lucide-react";

const NAVY = "hsl(217 63% 15%)";

const SUBS = [
  ["overview", "نظرة عامة", "Overview", LayoutDashboard],
  ["periods", "الفترات الضريبية", "VAT Periods", FileStack],
  ["requests", "طلبات البيانات", "Data Requests", ClipboardList],
  ["actions", "إجراءات مطلوبة", "Your Actions", BellRing],
  ["filing", "التقديم والسداد", "Filing & Payment", Receipt],
  ["zatca", "ملفات الهيئة", "ZATCA", Landmark],
];

function Card({ children, className = "", ...rest }) {
  return <div className={`bg-white rounded-xl border border-border p-4 ${className}`} {...rest}>{children}</div>;
}
function Kpi({ label, value, tid, tone = "" }) {
  return (
    <div className={`rounded-lg border border-border px-3 py-2.5 ${tone}`} data-testid={`cp-kpi-${tid}`}>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className="text-lg font-bold mt-0.5">{value}</div>
    </div>
  );
}
function fmt(n) {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  return isNaN(v) ? n : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function dueBadge(status, L) {
  if (!status) return null;
  const map = {
    OVERDUE: ["متجاوز", "Overdue", "bg-[hsl(354_60%_45%)]/12 text-[hsl(354_60%_40%)]"],
    DUE_SOON: ["وشيك", "Due soon", "bg-[hsl(41_72%_50%)]/18 text-[hsl(41_72%_32%)]"],
    OPEN: ["مفتوح", "Open", "bg-secondary text-muted-foreground"],
  };
  const m = map[status]; if (!m) return null;
  return <span className={`px-1.5 py-0.5 rounded text-[10px] ${m[2]}`}>{L(m[0], m[1])}</span>;
}

function useData(url) {
  const [d, setD] = useState(undefined);
  const load = useCallback(() => { api.get(url).then(({ data }) => setD(data)).catch(() => setD(null)); }, [url]);
  useEffect(() => { load(); }, [load]);
  return d;
}

export default function ClientPortal() {
  const { lang } = useI18n();
  const L = (ar, en) => (lang === "ar" ? ar : en);
  const [sub, setSub] = useState("overview");
  const [pres, setPres] = useState({});
  useEffect(() => { api.get("/client/presentation").then(({ data }) => setPres(data || {})).catch(() => {}); }, []);
  const accent = pres.primary_color || NAVY;
  const brand = pres.brand_name || "VATRdar";

  return (
    <div className="space-y-6" data-testid="client-portal">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold" style={{ color: accent }}>{L("بوابة الامتثال الضريبي", "Compliance Portal")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{L("متابعة فتراتك الضريبية ومتطلباتك وحالة التقديم والسداد.", "Track your VAT periods, requirements, filing and payment status.")}</p>
        </div>
        <div className="text-[11px] text-muted-foreground" data-testid="cp-brand">{brand}</div>
      </div>

      <div className="flex gap-1 flex-wrap" data-testid="cp-tabs">
        {SUBS.map(([k, ar, en, Icon]) => (
          <button key={k} data-testid={`cp-sub-${k}`} onClick={() => setSub(k)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${sub === k ? "text-white border-transparent" : "bg-white text-foreground/70 border-border hover:bg-secondary"}`}
            style={sub === k ? { backgroundColor: accent } : {}}>
            <Icon className="h-3.5 w-3.5" /> {L(ar, en)}
          </button>
        ))}
      </div>
      <div className="text-[10px] text-muted-foreground" data-testid="cp-readonly-banner">
        {L("عرض للقراءة فقط لبياناتك — الاعتمادات تُنفَّذ عبر فريقك الضريبي.", "Read-only view of your own data — approvals are actioned with your tax team.")}
      </div>

      {sub === "overview" && <Overview L={L} accent={accent} />}
      {sub === "periods" && <Periods L={L} />}
      {sub === "requests" && <Requests L={L} />}
      {sub === "actions" && <Actions L={L} />}
      {sub === "filing" && <Filing L={L} />}
      {sub === "zatca" && <Zatca L={L} />}
    </div>
  );
}

function Overview({ L, accent }) {
  const d = useData("/client/overview");
  if (d === undefined) return <Card>{L("جارٍ التحميل…", "Loading…")}</Card>;
  if (d === null) return <Card data-testid="cp-error">{L("تعذّر تحميل البيانات، يُرجى تحديث الصفحة.", "Couldn't load your data, please refresh.")}</Card>;
  const c = d.cards;
  const chart = d.by_stage.filter((x) => x.count > 0);
  const max = Math.max(1, ...chart.map((x) => x.count));
  return (
    <div className="space-y-4" data-testid="cp-overview">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Kpi tid="entities" label={L("المنشآت", "Entities")} value={c.entities} />
        <Kpi tid="periods" label={L("الفترات", "Periods")} value={c.periods_total} />
        <Kpi tid="awaiting-approval" label={L("بانتظار اعتمادك", "Awaiting your approval")} value={c.awaiting_your_approval} tone={c.awaiting_your_approval ? "bg-[hsl(41_72%_50%)]/8" : ""} />
        <Kpi tid="outstanding-requests" label={L("طلبات معلّقة", "Outstanding requests")} value={c.outstanding_requests} tone={c.outstanding_requests ? "bg-[hsl(41_72%_50%)]/8" : ""} />
        <Kpi tid="filed" label={L("فترات مُقدَّمة", "Filed periods")} value={c.filed_periods} />
        <Kpi tid="payable" label={L("ضريبة مستحقة", "VAT payable")} value={fmt(c.vat_payable)} />
        <Kpi tid="credit" label={L("رصيد دائن", "VAT credit")} value={fmt(c.vat_credit)} />
        <Kpi tid="zatca-open" label={L("ملفات الهيئة", "ZATCA open")} value={c.zatca_open} />
      </div>
      <Card>
        <div className="text-xs font-semibold text-muted-foreground uppercase mb-3">{L("الفترات حسب المرحلة", "Periods by stage")}</div>
        {chart.length ? (
          <div className="space-y-2" data-testid="cp-stage-chart">
            {chart.map((x) => (
              <div key={x.stage} className="flex items-center gap-2">
                <div className="w-36 text-[11px] text-foreground/70 truncate">{L(x.label.ar, x.label.en)}</div>
                <div className="flex-1 bg-secondary rounded-full h-3 overflow-hidden">
                  <div className="h-3 rounded-full" style={{ width: `${(x.count / max) * 100}%`, backgroundColor: accent }} />
                </div>
                <div className="w-8 text-end text-[11px] font-semibold">{x.count}</div>
              </div>
            ))}
          </div>
        ) : <div className="text-[11px] text-muted-foreground">{L("لا بيانات", "No data")}</div>}
      </Card>
    </div>
  );
}

function Periods({ L }) {
  const d = useData("/client/periods");
  if (d === undefined) return <Card>{L("جارٍ التحميل…", "Loading…")}</Card>;
  if (d === null) return <Card data-testid="cp-error">{L("تعذّر تحميل البيانات، يُرجى تحديث الصفحة.", "Couldn't load your data, please refresh.")}</Card>;
  return (
    <div className="space-y-2" data-testid="cp-periods">
      {d.periods.map((p) => (
        <Card key={p.id} className="flex flex-wrap items-center gap-3" data-testid={`cp-period-${p.id}`}>
          <div className="flex-1 min-w-[160px]">
            <div className="font-semibold text-sm">{p.label}</div>
            <div className="text-[11px] text-muted-foreground">{p.start_date} → {p.end_date}</div>
          </div>
          <span className="px-2 py-1 rounded-full text-[11px] font-semibold bg-secondary" data-testid={`cp-period-stage-${p.id}`}>
            {L(p.stage_label?.ar, p.stage_label?.en)}
          </span>
          {p.requires_your_action && (
            <span className="inline-flex items-center gap-1 text-[11px] text-[hsl(41_72%_32%)] bg-[hsl(41_72%_50%)]/18 px-2 py-1 rounded-full" data-testid={`cp-period-action-${p.id}`}>
              <AlertCircle className="h-3 w-3" /> {L("يتطلب اعتمادك", "Needs your approval")}
            </span>
          )}
          {p.outstanding_requests > 0 && (
            <span className="text-[11px] text-muted-foreground">{p.outstanding_requests} {L("طلب معلّق", "open requests")}</span>
          )}
          {p.filing && (
            <span className="text-[11px] text-muted-foreground">
              {p.filing.vat_payable > 0 ? `${L("مستحق", "Payable")}: ${fmt(p.filing.vat_payable)}` : ""}
              {p.filing.vat_credit > 0 ? `${L("دائن", "Credit")}: ${fmt(p.filing.vat_credit)}` : ""}
            </span>
          )}
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <CalendarClock className="h-3.5 w-3.5" /> {p.due_date || "—"} {dueBadge(p.due_status, L)}
          </div>
        </Card>
      ))}
      {!d.periods.length && <Card><div className="text-[11px] text-muted-foreground">{L("لا توجد فترات بعد.", "No periods yet.")}</div></Card>}
    </div>
  );
}

function Requests({ L }) {
  const d = useData("/client/data-requests");
  if (d === undefined) return <Card>{L("جارٍ التحميل…", "Loading…")}</Card>;
  if (d === null) return <Card data-testid="cp-error">{L("تعذّر تحميل البيانات، يُرجى تحديث الصفحة.", "Couldn't load your data, please refresh.")}</Card>;
  return (
    <Card data-testid="cp-requests">
      <div className="flex gap-2 text-[11px] mb-3">
        <span className="rounded px-2 py-1 bg-secondary">{L("الإجمالي", "Total")}: {d.summary.total}</span>
        <span className="rounded px-2 py-1 bg-[hsl(41_72%_50%)]/18 text-[hsl(41_72%_32%)]">{L("معلّقة", "Outstanding")}: {d.summary.outstanding}</span>
      </div>
      <div className="space-y-1" data-testid="cp-request-list">
        {d.items.map((i) => (
          <div key={i.id} className="flex items-center gap-2 text-[11px] rounded border border-border px-2 py-1.5">
            <span className={`px-1.5 py-0.5 rounded ${i.outstanding ? "bg-[hsl(41_72%_50%)]/18 text-[hsl(41_72%_32%)]" : "bg-[hsl(152_55%_45%)]/15 text-[hsl(152_55%_30%)]"}`}>
              {L(i.status_label?.ar || i.status, i.status_label?.en || i.status)}
            </span>
            <span className="bg-secondary px-1.5 py-0.5 rounded">{i.category}</span>
            <span className="flex-1 truncate">{i.label}</span>
            <span className="text-muted-foreground">{i.period_label}</span>
          </div>
        ))}
        {!d.items.length && <div className="text-[11px] text-muted-foreground">{L("لا توجد طلبات.", "No requests.")}</div>}
      </div>
    </Card>
  );
}

function Actions({ L }) {
  const d = useData("/client/actions");
  if (d === undefined) return <Card>{L("جارٍ التحميل…", "Loading…")}</Card>;
  if (d === null) return <Card data-testid="cp-error">{L("تعذّر تحميل البيانات، يُرجى تحديث الصفحة.", "Couldn't load your data, please refresh.")}</Card>;
  const none = d.action_count === 0;
  return (
    <div className="space-y-3" data-testid="cp-actions">
      <Card className="flex items-center gap-2">
        {none ? <CheckCircle2 className="h-5 w-5 text-[hsl(152_55%_40%)]" /> : <BellRing className="h-5 w-5 text-[hsl(41_72%_45%)]" />}
        <div className="text-sm font-semibold" data-testid="cp-action-count">
          {none ? L("لا توجد إجراءات مطلوبة منك حالياً.", "No actions required from you right now.")
            : L(`لديك ${d.action_count} إجراء/إجراءات بحاجة إلى انتباهك.`, `You have ${d.action_count} item(s) needing your attention.`)}
        </div>
      </Card>
      {d.outstanding_requests > 0 && (
        <Card data-testid="cp-action-requests"><div className="text-[11px]">{L("طلبات بيانات معلّقة", "Outstanding data requests")}: <b>{d.outstanding_requests}</b></div></Card>
      )}
      {d.period_approvals.map((a) => (
        <Card key={a.period_id} className="flex items-center gap-2 text-[11px]" data-testid={`cp-approval-${a.period_id}`}>
          <AlertCircle className="h-4 w-4 text-[hsl(41_72%_45%)]" />
          <span className="flex-1">{L("اعتماد الإقرار للفترة", "Approve the return for")}: <b>{a.label}</b></span>
          <span className="text-muted-foreground">{a.due_date || "—"}</span>
        </Card>
      ))}
      {d.zatca_approvals.map((a, n) => (
        <Card key={n} className="flex items-center gap-2 text-[11px]" data-testid={`cp-zatca-approval-${n}`}>
          <Landmark className="h-4 w-4 text-[hsl(41_72%_45%)]" />
          <span className="flex-1">{L("اعتماد ملف الهيئة", "Approve ZATCA case")}: <b>{a.reference}</b></span>
          <span className="text-muted-foreground">{a.due_date || "—"}</span>
        </Card>
      ))}
    </div>
  );
}

function Filing({ L }) {
  const d = useData("/client/filing");
  if (d === undefined) return <Card>{L("جارٍ التحميل…", "Loading…")}</Card>;
  if (d === null) return <Card data-testid="cp-error">{L("تعذّر تحميل البيانات، يُرجى تحديث الصفحة.", "Couldn't load your data, please refresh.")}</Card>;
  const t = d.totals;
  return (
    <div className="space-y-4" data-testid="cp-filing">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Kpi tid="f-payable" label={L("مستحق", "Payable")} value={fmt(t.vat_payable)} />
        <Kpi tid="f-credit" label={L("رصيد دائن", "Credit")} value={fmt(t.vat_credit)} />
        <Kpi tid="f-paid" label={L("مُسدَّد", "Paid")} value={fmt(t.confirmed_paid)} />
        <Kpi tid="f-outstanding" label={L("المتبقي", "Outstanding")} value={fmt(t.outstanding)} tone={t.outstanding > 0 ? "bg-[hsl(354_60%_45%)]/8" : ""} />
      </div>
      <Card>
        <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("الإقرارات", "Returns")}</div>
        <div className="space-y-1" data-testid="cp-returns">
          {d.returns.map((r) => (
            <div key={r.period_id} className="flex items-center gap-2 text-[11px] rounded border border-border px-2 py-1.5">
              <span className="flex-1">{r.period_label}</span>
              <span className={`px-1.5 py-0.5 rounded ${r.filed ? "bg-[hsl(152_55%_45%)]/15 text-[hsl(152_55%_30%)]" : "bg-secondary"}`}>{r.filed ? L("مُقدَّم", "Filed") : L("غير مُقدَّم", "Not filed")}</span>
              {r.vat_payable > 0 && <span className="text-muted-foreground">{L("مستحق", "Payable")}: {fmt(r.vat_payable)}</span>}
              {r.vat_credit > 0 && <span className="text-muted-foreground">{L("دائن", "Credit")}: {fmt(r.vat_credit)}</span>}
            </div>
          ))}
          {!d.returns.length && <div className="text-[11px] text-muted-foreground">{L("لا إقرارات.", "No returns.")}</div>}
        </div>
      </Card>
      {d.refunds.length > 0 && (
        <Card>
          <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("طلبات الاسترداد", "Refund requests")}</div>
          <div className="space-y-1" data-testid="cp-refunds">
            {d.refunds.map((r, n) => (
              <div key={n} className="flex items-center gap-2 text-[11px] rounded border border-border px-2 py-1.5">
                <span className="bg-secondary px-1.5 py-0.5 rounded">{r.status}</span>
                <span className="flex-1">{L("مطلوب", "Requested")}: {fmt(r.requested_amount)}</span>
                <span className="text-muted-foreground">{L("معتمد", "Approved")}: {fmt(r.approved_amount)}</span>
                <span className="text-muted-foreground">{L("مدفوع", "Paid")}: {fmt(r.paid_amount)}</span>
                <span className="text-muted-foreground">{r.deadline || "—"}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function Zatca({ L }) {
  const d = useData("/client/zatca");
  if (d === undefined) return <Card>{L("جارٍ التحميل…", "Loading…")}</Card>;
  if (d === null) return <Card data-testid="cp-error">{L("تعذّر تحميل البيانات، يُرجى تحديث الصفحة.", "Couldn't load your data, please refresh.")}</Card>;
  return (
    <Card data-testid="cp-zatca">
      <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{L("ملفات الهيئة", "ZATCA cases")}: {d.open} {L("مفتوح", "open")}</div>
      <div className="space-y-1" data-testid="cp-zatca-list">
        {d.cases.map((c, n) => (
          <div key={n} className="flex items-center gap-2 text-[11px] rounded border border-border px-2 py-1.5">
            <span className="font-mono">{c.reference}</span>
            <span className="bg-secondary px-1.5 py-0.5 rounded">{c.type}</span>
            <span className="flex-1 truncate">{c.title}</span>
            {c.requires_your_action && <span className="text-[hsl(41_72%_32%)] bg-[hsl(41_72%_50%)]/18 px-1.5 py-0.5 rounded">{L("يتطلب اعتمادك", "Needs your approval")}</span>}
            <span className="text-muted-foreground">{c.deadline || "—"}</span>
            {dueBadge(c.deadline_status, L)}
          </div>
        ))}
        {!d.cases.length && <div className="text-[11px] text-muted-foreground">{L("لا توجد ملفات مرتبطة بك.", "No cases linked to you.")}</div>}
      </div>
    </Card>
  );
}
